// ============================================================================
// @cpg/common — slim cross-stack primitives shared by backend AND frontend.
//
// Contains ONLY: enums, constants, and pure utility functions.
// No DTOs, no schemas, no response interfaces, no runtime dependencies.
//
//   - Backend DTO classes (class-validator) live in each service.
//   - Frontend response interfaces live in each app's `src/api/` layer.
//   - Both reference the enums/constants/utils here so they never drift.
// ============================================================================

// ─── Enums ──────────────────────────────────────────────────────────────────

export enum Currency {
  BTC = 'BTC',
  ETH = 'ETH',
  USDT_ERC20 = 'USDT_ERC20',
  USDC_ERC20 = 'USDC_ERC20',
  TRX = 'TRX',
  USDT_TRC20 = 'USDT_TRC20',
}

export enum Network {
  BITCOIN = 'BITCOIN',
  ETHEREUM = 'ETHEREUM',
  TRON = 'TRON',
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  AWAITING_CONFIRMATION = 'AWAITING_CONFIRMATION',
  CONFIRMED = 'CONFIRMED',
  OVERPAID = 'OVERPAID',
  UNDERPAID = 'UNDERPAID',
  EXPIRED = 'EXPIRED',
  FAILED = 'FAILED',
  REFUNDED = 'REFUNDED',
}

export enum MerchantStatus {
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  PENDING_KYB = 'PENDING_KYB',
}

export enum WebhookEvent {
  INVOICE_CONFIRMING = 'invoice.confirming',
  // The exact target amount confirmed on-chain — the invoice is paid, before
  // any settlement sweep is attempted (feature-034). The invoice analogue of
  // CHANNEL_PAYMENT_CONFIRMED.
  INVOICE_CONFIRMED = 'invoice.confirmed',
  INVOICE_PARTIALLY_PAID = 'invoice.partially_paid',
  // A settlement sweep is actually under way (feature-034) — fired the tick
  // InvoiceSweepService successfully dispatches the transfer, not merely when
  // the invoice became sweep-eligible. The invoice analogue of
  // CHANNEL_PAYMENT_SETTLING.
  INVOICE_SETTLING = 'invoice.settling',
  INVOICE_COMPLETED = 'invoice.completed',
  INVOICE_EXPIRED = 'invoice.expired',
  INVOICE_ACTION_REQUIRED = 'invoice.action_required',
  INVOICE_REJECTED = 'invoice.rejected',
  INVOICE_REFUNDED = 'invoice.refunded',
  WEBHOOK_TEST = 'webhook.test',
  // Payment channels (feature-031). One event per ChannelPaymentStatus — a
  // failed settlement fires no *second* notification (merchants already saw
  // SETTLING and keep seeing it; see ChannelPaymentSubStatus.SETTLEMENT_FAILED).
  CHANNEL_PAYMENT_CONFIRMING = 'channel_payment.confirming',
  CHANNEL_PAYMENT_CONFIRMED = 'channel_payment.confirmed',
  CHANNEL_PAYMENT_SETTLING = 'channel_payment.settling',
  CHANNEL_PAYMENT_COMPLETED = 'channel_payment.completed',
  CHANNEL_PAYMENT_ACTION_REQUIRED = 'channel_payment.action_required',
  CHANNEL_PAYMENT_REFUNDED = 'channel_payment.refunded',
  CHANNEL_PAYMENT_CANCELLED = 'channel_payment.cancelled',
  CHANNEL_PAYMENT_FAILED = 'channel_payment.failed',
}

export enum ApiKeyScope {
  INVOICES_READ = 'invoices:read',
  INVOICES_WRITE = 'invoices:write',
  ASSETS_READ = 'assets:read',
  // Payment channels (feature-031).
  CHANNELS_READ = 'channels:read',
  CHANNELS_WRITE = 'channels:write',
}

export enum ApiKeyStatus {
  ACTIVE = 'ACTIVE',
  REVOKED = 'REVOKED',
}

export enum WebhookDeliveryStatus {
  PENDING = 'PENDING',
  SUCCEEDED = 'SUCCEEDED',
  EXHAUSTED = 'EXHAUSTED',
}

export enum AssetNetwork {
  BITCOIN = 'BITCOIN',
  ETHEREUM = 'ETHEREUM',
  BASE = 'BASE',
  TRON = 'TRON',
  SOLANA = 'SOLANA',
  BNB = 'BNB',
  TON = 'TON',
}

export enum AccountStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  FAILED = 'FAILED',
}

export enum TransientWalletStatus {
  AVAILABLE = 'AVAILABLE',
  OCCUPIED = 'OCCUPIED',
  SWEPT = 'SWEPT', // lifecycle is strictly forward (AVAILABLE → OCCUPIED → SWEPT); wallets never return to AVAILABLE
}

export enum RateSource {
  BINANCE = 'BINANCE',
  MEXC = 'MEXC',
  FIXED = 'FIXED',
}

export enum InvoiceStatus {
  NEW = 'NEW',
  CONFIRMING = 'CONFIRMING',
  // A short deposit accumulating toward the invoiced amount, mid-TTL. Carries
  // no sub-status and is never ACTION_REQUIRED (feature-018).
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  // The exact target amount confirmed on-chain — the invoice is paid and
  // eligible to be swept, but nothing has been swept yet (feature-034). Every
  // path that used to write SETTLING directly now writes CONFIRMED; SETTLING
  // is reached only once InvoiceSweepService actually dispatches a sweep.
  // A pure lifecycle/notification milestone — unlike a channel payment's
  // CONFIRMED it freezes nothing, since an invoice fixes its amount at
  // creation.
  CONFIRMED = 'CONFIRMED',
  SETTLING = 'SETTLING',
  COMPLETED = 'COMPLETED',
  ACTION_REQUIRED = 'ACTION_REQUIRED',
  REFUNDED = 'REFUNDED', // reserved for the future manual-resolution feature
  EXPIRED = 'EXPIRED',
  // Terminal (feature-019): written only by the backoffice AML Reject action,
  // never by derivation. Checkout reads it as CONFIRMING, same as HIGH_RISK.
  REJECTED = 'REJECTED',
}

/** Set only together with InvoiceStatus.ACTION_REQUIRED; null otherwise. */
export enum InvoiceSubStatus {
  UNDERPAID = 'UNDERPAID',
  OVERPAID = 'OVERPAID',
  PAID_LATE = 'PAID_LATE',
  HIGH_RISK = 'HIGH_RISK',
  SETTLEMENT_FAILED = 'SETTLEMENT_FAILED',
}

/**
 * Merchant-facing invoice status (merchant-api + apps/merchant). Same shape
 * as the internal InvoiceStatus, but a settlement failure never surfaces as
 * ACTION_REQUIRED — the required action is the platform's, not the
 * merchant's — so those invoices read as SETTLING (with sub-status DELAYED).
 */
export enum MerchantInvoiceStatus {
  NEW = 'NEW',
  CONFIRMING = 'CONFIRMING',
  // Identity mapping of InvoiceStatus.PARTIALLY_PAID — top-up is
  // merchant-visible as-is, nothing to relabel (feature-018).
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  // Identity mapping of InvoiceStatus.CONFIRMED (feature-034) — as safe to
  // show a merchant as CONFIRMING. Every "filter by Settling" surface also
  // matches CONFIRMED invoices, so a merchant filtering by "Settling" keeps
  // seeing invoices on their way there.
  CONFIRMED = 'CONFIRMED',
  SETTLING = 'SETTLING',
  COMPLETED = 'COMPLETED',
  ACTION_REQUIRED = 'ACTION_REQUIRED',
  REFUNDED = 'REFUNDED',
  EXPIRED = 'EXPIRED',
  // Identity mapping of InvoiceStatus.REJECTED — terminal (feature-019).
  REJECTED = 'REJECTED',
}

/**
 * Merchant-facing invoice sub-status. DELAYED pairs only with SETTLING
 * (internal SETTLEMENT_FAILED); the rest pair only with ACTION_REQUIRED.
 * UNDER_REVIEW relabels the internal HIGH_RISK, matching the transaction
 * FROZEN → UNDER_REVIEW precedent.
 */
export enum MerchantInvoiceSubStatus {
  UNDERPAID = 'UNDERPAID',
  OVERPAID = 'OVERPAID',
  PAID_LATE = 'PAID_LATE',
  UNDER_REVIEW = 'UNDER_REVIEW',
  DELAYED = 'DELAYED',
}

/**
 * Customer-facing invoice status (payment-api public endpoints +
 * apps/checkout). The customer's lifecycle ends at PAID the moment the exact
 * deposit completes — settlement (SETTLING / COMPLETED / settlement-failed)
 * is invisible plumbing and all reads as PAID; HIGH_RISK reads as
 * CONFIRMING.
 */
export enum CheckoutInvoiceStatus {
  AWAITING_PAYMENT = 'AWAITING_PAYMENT',
  CONFIRMING = 'CONFIRMING',
  // Identity mapping of InvoiceStatus.PARTIALLY_PAID — top-up is
  // customer-facing as-is, nothing to relabel (feature-018).
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  PAID = 'PAID',
  UNDERPAID = 'UNDERPAID',
  OVERPAID = 'OVERPAID',
  PAID_LATE = 'PAID_LATE',
  REFUNDED = 'REFUNDED',
  EXPIRED = 'EXPIRED',
}

/**
 * Customer-facing refund status (payment-api public endpoints + apps/checkout).
 * Mapping from internal RefundStatus:
 *   - OPEN → AVAILABLE
 *   - CLAIMED, FAILED → PENDING (a failed refund is the platform's problem;
 *     customer keeps seeing "refund pending")
 *   - COMPLETED → COMPLETED
 *   - CANCELLED → omitted entirely from the response (not a mapped value;
 *     callers must branch on this before mapping)
 */
export enum CheckoutRefundStatus {
  AVAILABLE = 'AVAILABLE',
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
}

/**
 * Merchant-facing refund status (merchant-api + apps/merchant).
 * Mapping from internal RefundStatus:
 *   - OPEN → AVAILABLE
 *   - CLAIMED, FAILED → PENDING (a failed refund is the platform's problem;
 *     merchant keeps seeing "refund pending")
 *   - COMPLETED → COMPLETED
 *   - CANCELLED → omitted entirely from the response (not a mapped value;
 *     callers must branch on this before mapping)
 */
export enum MerchantRefundStatus {
  AVAILABLE = 'AVAILABLE',
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
}

/** Internal (provider-agnostic) transaction status; FROZEN = funds held for AML/policy review. */
export enum TransactionStatus {
  PENDING = 'PENDING',
  CONFIRMING = 'CONFIRMING',
  COMPLETED = 'COMPLETED',
  FROZEN = 'FROZEN',
  FAILED = 'FAILED',
}

/** Our classification of a transfer by which of our wallets/accounts its peers resolve to. */
export enum TransactionOperation {
  DEPOSIT = 'DEPOSIT', // external → transient wallet
  SETTLEMENT = 'SETTLEMENT', // transient wallet → account
  GAS_FUND = 'GAS_FUND', // platform gas vault → transient wallet (top-up for network fee shortfall)
  GAS_RECLAIM = 'GAS_RECLAIM', // transient wallet → platform gas vault (reclaim of leftover gas)
  REFUND = 'REFUND', // transient wallet → external
  WITHDRAW = 'WITHDRAW', // account → external
  DUST = 'DUST',
}

export enum RefundStatus {
  OPEN = 'OPEN',
  CLAIMED = 'CLAIMED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export enum RefundInitiator {
  AUTO = 'AUTO',
  MERCHANT = 'MERCHANT',
  // A backoffice AML_OFFICER's Refund resolution action (feature-019).
  BACKOFFICE = 'BACKOFFICE',
}

/** Who supplied the payout address at claim time. */
export enum RefundClaimant {
  CUSTOMER = 'CUSTOMER',
  MERCHANT = 'MERCHANT',
}

/** Withdrawal lifecycle status (feature-030). */
export enum WithdrawalStatus {
  PENDING_REVIEW = 'PENDING_REVIEW',
  APPROVED = 'APPROVED',
  SENDING = 'SENDING',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED',
  FAILED = 'FAILED',
}

/** Withdrawal limit breach type (feature-030). */
export enum WithdrawalLimitBreach {
  PER_TX_MAX = 'PER_TX_MAX',
  DAILY_MAX = 'DAILY_MAX',
}

/**
 * Per-sub-status merchant policy action for an eligible ACTION_REQUIRED
 * invoice. Deliberately an enum, not a boolean, so a future action extends
 * the vocabulary without a schema change. MANUAL is valid API/UI vocabulary
 * but is never persisted — see InvoicePolicy (a row's absence means
 * MANUAL).
 */
export enum InvoicePolicyAction {
  MANUAL = 'MANUAL',
  AUTO_REFUND = 'AUTO_REFUND',
  // Accepts the deposited funds as-is (feature-018). For UNDERPAID this is
  // gated by the policy row's max-shortfall percent; unconditional for
  // OVERPAID/PAID_LATE.
  AUTO_COMPLETE = 'AUTO_COMPLETE',
}

/**
 * Who resolved an ACTION_REQUIRED invoice via Accept & Complete
 * (feature-018) — RefundInitiator's shape, kept as its own enum so the two
 * vocabularies can drift independently.
 */
export enum InvoiceResolutionInitiator {
  AUTO = 'AUTO',
  MERCHANT = 'MERCHANT',
}

export enum FiatCurrency {
  USD = 'USD',
}

/**
 * Direction of a ledger (merchant/project) deposit-style entry. Only
 * DEPOSIT is written by feature-016; WITHDRAW is reserved for a future
 * withdrawal/consolidation feature.
 */
export enum LedgerOperation {
  DEPOSIT = 'DEPOSIT',
  WITHDRAW = 'WITHDRAW',
}

/**
 * Backoffice admin role (feature-019). SUPERADMIN passes every `@Roles()`
 * guard check regardless of which role(s) an endpoint requires; the other
 * roles are additive and endpoint-scoped (merchant reads, merchant
 * create/edit, invoice/transaction/refund reads, AML high-risk queue +
 * resolution actions, respectively).
 */
export enum AdminRole {
  SUPERADMIN = 'SUPERADMIN',
  MERCHANTS_VIEWER = 'MERCHANTS_VIEWER',
  MERCHANTS_MANAGER = 'MERCHANTS_MANAGER',
  INVOICES_VIEWER = 'INVOICES_VIEWER',
  // Write-capable invoice action(s) beyond plain viewing — currently just
  // manual settlement retry (ACTION_REQUIRED/SETTLEMENT_FAILED -> SETTLING,
  // once ops has fixed whatever kept declining the sweep/gas-fund). Separate
  // from INVOICES_VIEWER per this codebase's viewer/manager split convention
  // (mirrors MERCHANTS_VIEWER/MERCHANTS_MANAGER).
  INVOICES_MANAGER = 'INVOICES_MANAGER',
  AML_OFFICER = 'AML_OFFICER',
  FINANCE_VIEWER = 'FINANCE_VIEWER',
  CONFIG_MANAGER = 'CONFIG_MANAGER',
  TECH_VIEWER = 'TECH_VIEWER',
  // Payment channels (feature-031) — same viewer/manager split as
  // MERCHANTS_VIEWER/MERCHANTS_MANAGER; forcing/re-running a settlement is
  // restricted to CHANNELS_MANAGER.
  CHANNELS_VIEWER = 'CHANNELS_VIEWER',
  CHANNELS_MANAGER = 'CHANNELS_MANAGER',
}

/**
 * Dashboard period selection (feature-021). Used by the backoffice dashboard
 * query DTOs and the frontend period switcher to specify the time range for
 * financial and operational metrics.
 */
export enum DashboardPeriod {
  H24 = '24h',
  D7 = '7d',
  D30 = '30d',
  D90 = '90d',
}

/**
 * An AML_OFFICER's resolution action on an ACTION_REQUIRED/HIGH_RISK invoice
 * (feature-019) — REJECT terminates the invoice as REJECTED, ACCEPT
 * re-judges it through the ordinary deposit-outcome rules, REFUND opens a
 * BACKOFFICE-initiated refund. Last action wins; see the invoice's
 * `aml_resolution_action` audit column.
 */
export enum AmlResolutionAction {
  REJECT = 'REJECT',
  ACCEPT = 'ACCEPT',
  REFUND = 'REFUND',
}

/**
 * Invoice flow lifecycle steps for observability and auditing (feature-024).
 * Each step represents a distinct milestone in the invoice's journey from
 * creation through completion or expiration.
 */
export enum InvoiceFlowStep {
  INVOICE_CREATED = 'INVOICE_CREATED',
  RATE_FIXED = 'RATE_FIXED',
  WALLET_OCCUPIED = 'WALLET_OCCUPIED',
  DEPOSIT_DETECTED = 'DEPOSIT_DETECTED',
  DEPOSIT_CONFIRMED = 'DEPOSIT_CONFIRMED',
  INVOICE_STATUS_CHANGED = 'INVOICE_STATUS_CHANGED',
  GAS_CHECK_PASSED = 'GAS_CHECK_PASSED',
  GAS_FUNDED = 'GAS_FUNDED',
  SWEEP_REQUESTED = 'SWEEP_REQUESTED',
  SWEPT = 'SWEPT',
  LEDGERS_CREDITED = 'LEDGERS_CREDITED',
  INVOICE_COMPLETED = 'INVOICE_COMPLETED',
  INVOICE_EXPIRED = 'INVOICE_EXPIRED',
  RESOLUTION_APPLIED = 'RESOLUTION_APPLIED',
  REFUND_OPENED = 'REFUND_OPENED',
  REFUND_CLAIMED = 'REFUND_CLAIMED',
  REFUND_SENT = 'REFUND_SENT',
  WEBHOOK_QUEUED = 'WEBHOOK_QUEUED',
  WEBHOOK_DELIVERED = 'WEBHOOK_DELIVERED',
  WEBHOOK_EXHAUSTED = 'WEBHOOK_EXHAUSTED',
}

// ─── Payment channels (feature-031) ────────────────────────────────────────────
// New values throughout, not reuses of the invoice-domain enums above — same
// per-domain-own-enum reasoning as InvoiceResolutionInitiator / RefundInitiator.

/** Channel provisioning lifecycle. The address exists for ACTIVE and beyond; ARCHIVED never changes deposit handling. */
export enum ChannelStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  FAILED = 'FAILED',
  ARCHIVED = 'ARCHIVED',
}

/** Internal (provider-agnostic) channel payment lifecycle. */
export enum ChannelPaymentStatus {
  CONFIRMING = 'CONFIRMING',
  CONFIRMED = 'CONFIRMED',
  SETTLING = 'SETTLING',
  COMPLETED = 'COMPLETED',
  ACTION_REQUIRED = 'ACTION_REQUIRED',
  REFUNDED = 'REFUNDED',
  CANCELLED = 'CANCELLED',
  FAILED = 'FAILED',
}

/** Set only together with ChannelPaymentStatus.ACTION_REQUIRED; null otherwise. */
export enum ChannelPaymentSubStatus {
  HIGH_RISK = 'HIGH_RISK',
  SETTLEMENT_FAILED = 'SETTLEMENT_FAILED',
}

/** Channel settlement (sweep batch) lifecycle. */
export enum ChannelSettlementStatus {
  PENDING = 'PENDING',
  SENDING = 'SENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

/**
 * Merchant-facing channel payment status. Same shape as the internal
 * ChannelPaymentStatus, but a settlement failure never surfaces as
 * ACTION_REQUIRED — the required action is the platform's, not the
 * merchant's — so those payments read as SETTLING (with sub-status DELAYED),
 * same relabeling as MerchantInvoiceStatus/MerchantInvoiceSubStatus.
 */
export enum MerchantChannelPaymentStatus {
  CONFIRMING = 'CONFIRMING',
  CONFIRMED = 'CONFIRMED',
  SETTLING = 'SETTLING',
  COMPLETED = 'COMPLETED',
  ACTION_REQUIRED = 'ACTION_REQUIRED',
  REFUNDED = 'REFUNDED',
  CANCELLED = 'CANCELLED',
  FAILED = 'FAILED',
}

/**
 * Merchant-facing channel payment sub-status. DELAYED pairs only with
 * SETTLING (internal SETTLEMENT_FAILED); UNDER_REVIEW pairs only with
 * ACTION_REQUIRED (internal HIGH_RISK) — mirrors MerchantInvoiceSubStatus.
 */
export enum MerchantChannelPaymentSubStatus {
  UNDER_REVIEW = 'UNDER_REVIEW',
  DELAYED = 'DELAYED',
}

/**
 * Merchant-facing channel settlement status. The internal PENDING/SENDING/
 * FAILED states all read as "still settling" — a failed sweep is the
 * platform's problem to retry, never the merchant's, so it never surfaces as
 * a distinct failure (spec: "The one status with no notification is a failed
 * settlement, because merchants never see it as anything but an ongoing
 * settlement."). Only COMPLETED reads as distinct.
 */
export enum MerchantChannelSettlementStatus {
  SETTLING = 'SETTLING',
  COMPLETED = 'COMPLETED',
}

/**
 * Channel lifecycle steps for observability and auditing (feature-031),
 * mirroring InvoiceFlowStep's granularity for structured flow logging
 * (channel-flow-logger.ts / humanizeFlowStep).
 */
export enum ChannelFlowStep {
  CHANNEL_CREATED = 'CHANNEL_CREATED',
  CHANNEL_PROVISIONED = 'CHANNEL_PROVISIONED',
  CHANNEL_PROVISIONING_FAILED = 'CHANNEL_PROVISIONING_FAILED',
  DEPOSIT_DETECTED = 'DEPOSIT_DETECTED',
  DEPOSIT_CONFIRMED = 'DEPOSIT_CONFIRMED',
  CHANNEL_PAYMENT_STATUS_CHANGED = 'CHANNEL_PAYMENT_STATUS_CHANGED',
  PAYMENT_CANCELLED = 'PAYMENT_CANCELLED',
  SETTLEMENT_OPENED = 'SETTLEMENT_OPENED',
  GAS_CHECK_PASSED = 'GAS_CHECK_PASSED',
  GAS_FUNDED = 'GAS_FUNDED',
  SWEEP_REQUESTED = 'SWEEP_REQUESTED',
  SWEPT = 'SWEPT',
  LEDGERS_CREDITED = 'LEDGERS_CREDITED',
  SETTLEMENT_COMPLETED = 'SETTLEMENT_COMPLETED',
  SETTLEMENT_FAILED = 'SETTLEMENT_FAILED',
  RESOLUTION_APPLIED = 'RESOLUTION_APPLIED',
  REFUND_OPENED = 'REFUND_OPENED',
  REFUND_CLAIMED = 'REFUND_CLAIMED',
  REFUND_SENT = 'REFUND_SENT',
  WEBHOOK_QUEUED = 'WEBHOOK_QUEUED',
  WEBHOOK_DELIVERED = 'WEBHOOK_DELIVERED',
  WEBHOOK_EXHAUSTED = 'WEBHOOK_EXHAUSTED',
}

// ─── Utils ────────────────────────────────────────────────────────────────────
// One function per file (see e.g. compare-decimals.ts) — re-exported here so
// consumers keep importing everything from '@cpg/common' unchanged.

export { trimDecimals } from './trim-decimals';
export { formatAmount } from './format-amount';
export { isUUID } from './is-uuid';
export { isValidAddressForNetwork } from './is-valid-address-for-network';
export { isValidDestinationTag } from './is-valid-destination-tag';
export { clamp } from './clamp';
export { trimDecimalZeros } from './trim-decimal-zeros';
export { divideDecimalCeil } from './divide-decimal-ceil';
export { compareDecimals } from './compare-decimals';
export { multiplyDecimals } from './multiply-decimals';
export { addDecimals } from './add-decimals';
export { subtractDecimals } from './subtract-decimals';
export { computeCryptoFee } from './compute-crypto-fee';
export { toUsdAmount } from './to-usd-amount';
export { recalculateInvoiceAmounts } from './recalculate-invoice-amounts';
export { isWithinShortfallTolerance } from './is-within-shortfall-tolerance';
export { summarizeAmlScreening, extractAmlScreenedAt, type AmlScreeningSummary } from './summarize-aml-screening';

// ─── Constants ────────────────────────────────────────────────────────────────

export const NETWORK_BY_CURRENCY: Readonly<Record<Currency, Network>> = {
  [Currency.BTC]: Network.BITCOIN,
  [Currency.ETH]: Network.ETHEREUM,
  [Currency.USDT_ERC20]: Network.ETHEREUM,
  [Currency.USDC_ERC20]: Network.ETHEREUM,
  [Currency.TRX]: Network.TRON,
  [Currency.USDT_TRC20]: Network.TRON,
} as const;

export const REQUIRED_CONFIRMATIONS: Readonly<Record<Network, number>> = {
  [Network.BITCOIN]: 3,
  [Network.ETHEREUM]: 12,
  [Network.TRON]: 20,
} as const;

export const DEFAULT_PAYMENT_TTL_SECONDS = 3_600;

export const MAX_PAGE_SIZE = 100;
export const DEFAULT_PAGE_SIZE = 20;

/**
 * Known Fireblocks-enforced minimum on-chain BTC transfer amount, in native
 * units (582 satoshis, from Fireblocks' "Known minimums" documentation) — an
 * amount below this can never actually be sent (Fireblocks' own console/API
 * rejects it). Read by the gas station's BITCOIN funding floor (payment-async's
 * `resolveGasFundRequirement`), so a top-up is never itself too small to send.
 * Deliberately NOT enforced at invoice creation: merchants may legitimately
 * want to accept a sub-minimum quote, e.g. a customer topping up an existing
 * underpaid invoice.
 */
export const BTC_MIN_NATIVE_AMOUNT = '0.00000582'; // 582 satoshis

/**
 * Solana's rent-exempt minimum balance for a basic account: 2,039,280
 * lamports (SOL has 9 decimals). An account whose balance drops below this
 * is subject to garbage collection by the Solana runtime, so a transient
 * wallet's SOL balance must never be swept/spent down past it. The gas
 * station's SOLANA funding requirement (payment-async's
 * `resolveGasFundRequirement`) reserves this on top of the buffered network
 * fee — it's money that must stay on the wallet, not a cost paid to anyone,
 * so it's kept separate from BTC_MIN_NATIVE_AMOUNT above (a floor on what
 * CAN be sent in one transfer, not an amount that must be withheld on the
 * account).
 */
export const SOLANA_ACCOUNT_RENT_FEE = '0.00203928'; // 2,039,280 lamports
